// src/components/Hero.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ChevronDown, Wind } from "lucide-react";

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[var(--aerzen-blue)]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-[var(--aerzen-silver)]/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* LEFT CONTENT */}
          <div className="space-y-8 text-left">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-[var(--aerzen-blue)]/10 rounded-full">
              <Wind className="h-4 w-4 text-[var(--aerzen-blue)]" />
              <span className="text-sm font-medium text-[var(--aerzen-blue)]">
                Designed for Homes, Offices & Institutional Spaces
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[var(--aerzen-black)] leading-tight">
              Air Purification Systems
              <br />
              <span className="text-[var(--aerzen-blue)]">
                Engineered for Indian Air
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-[var(--aerzen-black)]/70 leading-relaxed max-w-xl">
              AERZEN AIR™ designs high-performance air purification solutions for
              homes, offices, schools, hospitals, and public infrastructure —
              engineered specifically for India’s high-AQI conditions, seasonal
              smog, and urban pollution.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate("/prebook")}
                size="lg"
                className="bg-[var(--aerzen-blue)] hover:bg-[#2d5bc4] text-white text-base px-10 py-7 rounded-xl shadow-xl"
              >
                Pre-Book for Home & Office
              </Button>

              <Button
                onClick={() => navigate("/institutional-solutions")}
                size="lg"
                variant="outline"
                className="border-2 border-[var(--aerzen-black)] text-[var(--aerzen-black)] hover:bg-[var(--aerzen-black)] hover:text-white text-base px-8 py-7 rounded-xl"
              >
                Institutional Use Cases
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[var(--aerzen-silver)]">
              <Stat value="99.97%" label="PM2.5 Filtration" />
              <Stat value="High CADR" label="Industrial-Grade Airflow" />
              <Stat value="2-Year" label="Warranty" />
            </div>

            <p className="text-[var(--aerzen-black)]/70 text-sm pt-4">
              Suitable for Indian homes, offices, schools, clinics, and
              pollution-sensitive environments.
            </p>
          </div>

          {/* RIGHT CONTENT — HERO IMAGE */}
          <div className="relative">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl bg-white">
              <img
                src="/images/hero/hero-banner.jpg"
                alt="AERZEN AIR™ Air Purification Systems for Indian Homes and Institutions"
                className="w-full h-auto object-cover rounded-2xl"
                loading="eager"
              />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-6 w-6 text-[var(--aerzen-black)]/40" />
        </div>
      </div>
    </section>
  );
};

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="text-3xl font-bold text-[var(--aerzen-blue)]">
        {value}
      </div>
      <div className="text-xs text-[var(--aerzen-black)]/60 mt-1">
        {label}
      </div>
    </div>
  );
}
